/**
 * Transactions Table Component
 * Displays transaction history with fee details using rowspan for transaction grouping
 */
import React, { Component } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import NewFeeReciept from '../../finance/newFeeReciept';
import LoadingComponent from '../../loadingComponent';

export class TransactionsTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            transactions: [],
            flattenedData: [],
            isLoading: false,
            isEnableReceipt: false,
            receiptData: []
        };
        
        // Create refs for scroll synchronization
        this.dataScrollRef = React.createRef();
        this.grandTotalScrollRef = React.createRef();
        
        // Bind template methods
        this.snoBodyTemplate = this.snoBodyTemplate.bind(this);
        this.receiptDateBodyTemplate = this.receiptDateBodyTemplate.bind(this);
        this.transactionIdBodyTemplate = this.transactionIdBodyTemplate.bind(this);
        this.receivingAccountBodyTemplate = this.receivingAccountBodyTemplate.bind(this);
        this.paymentModeBodyTemplate = this.paymentModeBodyTemplate.bind(this);
        this.feeTypeBodyTemplate = this.feeTypeBodyTemplate.bind(this);
        this.paidAmountBodyTemplate = this.paidAmountBodyTemplate.bind(this);
        this.totalBodyTemplate = this.totalBodyTemplate.bind(this);
        this.actionsBodyTemplate = this.actionsBodyTemplate.bind(this);
        this.rowClassName = this.rowClassName.bind(this);
        this.syncScroll = this.syncScroll.bind(this);
        this.processTransactionsData = this.processTransactionsData.bind(this);
    }

    async componentDidMount() {
        console.log('TransactionsTable component mounted');

        // Check if transactionsData is already passed as prop
        const { transactionsData, transactionsLoading } = this.props;

        if (transactionsData) {
            // Use data from props
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false) {
            // Data already loaded but empty
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
        // If transactionsLoading is true, wait for componentDidUpdate

        // Set up scroll synchronization after a short delay to ensure DOM is ready
        setTimeout(() => {
            this.attachScrollListener();
        }, 100);
    }

    processTransactionsData = (data) => {
        console.log('Processing transactions data:', data);
        
        // Access the collections property
        const transactions = data?.collections;
        console.log('Transactions from data.collections:', transactions);

        if (transactions && Array.isArray(transactions) && transactions.length > 0) {
            console.log('Processing', transactions.length, 'transactions');
            const flattened = this.flattenTransactions(transactions);
            console.log('Flattened result:', flattened.length, 'rows');
            
            this.setState({
                transactions: transactions,
                flattenedData: flattened,
                isLoading: false
            }, () => {
                console.log('State updated - transactions:', this.state.transactions.length, 'flattenedData:', this.state.flattenedData.length);
                // Re-attach scroll listener after data loads
                setTimeout(() => {
                    this.attachScrollListener();
                }, 100);
            });
        } else {
            // No data from props
            console.warn('No valid transactions in data.collections');
            this.setState({
                transactions: [],
                flattenedData: [],
                isLoading: false
            });
        }
    }

    attachScrollListener = () => {
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            // Remove existing listener first to avoid duplicates
            dataWrapper.removeEventListener('scroll', this.syncScroll);
            // Add new listener
            dataWrapper.addEventListener('scroll', this.syncScroll);
            console.log('Scroll listener attached');
        }
    }

    async componentDidUpdate(prevProps) {
        // Check if transactionsData prop has changed
        const { transactionsData, transactionsLoading } = this.props;
        
        if (transactionsData !== prevProps.transactionsData && transactionsData) {
            this.processTransactionsData(transactionsData);
        } else if (transactionsLoading === false && prevProps.transactionsLoading === true && !transactionsData) {
            // Loading finished but no data
            this.setState({ isLoading: false, transactions: [], flattenedData: [] });
        }
    }

    componentWillUnmount() {
        // Clean up scroll event listener
        const dataWrapper = this.dataScrollRef.current?.querySelector('.p-datatable-wrapper');
        if (dataWrapper) {
            dataWrapper.removeEventListener('scroll', this.syncScroll);
        }
    }

    syncScroll(e) {
        const scrollLeft = e.target.scrollLeft;
        const grandTotalWrapper = this.grandTotalScrollRef.current;
        if (grandTotalWrapper) {
            const grandTotalInner = grandTotalWrapper.querySelector('div');
            if (grandTotalInner) {
                grandTotalInner.style.transform = `translateX(-${scrollLeft}px)`;
                console.log('Syncing scroll - scrollLeft:', scrollLeft);
            }
        }
    }

    flattenTransactions = (transactions) => {
        console.log('flattenTransactions called with:', transactions);
        
        if (!transactions || !Array.isArray(transactions)) {
            console.warn('Invalid transactions data:', transactions);
            return [];
        }
        
        const flattened = [];
        transactions.forEach((transaction, transIndex) => {
            // Check if feePaidDetails exists and is an array
            if (!transaction.feePaidDetails || !Array.isArray(transaction.feePaidDetails)) {
                console.warn('Transaction missing feePaidDetails:', transaction);
                return; // Skip this transaction
            }
            
            const totalFeeDetails = transaction.feePaidDetails.length;
            transaction.feePaidDetails.forEach((feeDetail, feeIndex) => {
                flattened.push({
                    ...transaction,
                    feeDetail: feeDetail,
                    transIndex: transIndex,
                    feeIndex: feeIndex,
                    isFirstRow: feeIndex === 0,
                    isLastRow: feeIndex === totalFeeDetails - 1,
                    rowSpan: totalFeeDetails,
                    uniqueKey: `${transaction._id}-${feeIndex}`
                });
            });
        });
        
        console.log('Flattened data:', flattened);
        return flattened;
    };

    formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    formatDateTime = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    calculateGrandTotal = () => {
        const { transactions } = this.state;
        return transactions.reduce((sum, transaction) => sum + (transaction.amountPaid || 0), 0);
    };

    // Body Template Methods for DataTable Columns
    snoBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        return (
            <span style={{ display: 'block', textAlign: 'center' }}>
                {rowData.transIndex + 1}
            </span>
        );
    }

    receiptDateBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        return <span>{rowData?.transactionDate ? this.formatDate(rowData?.transactionDate) : this.formatDate(rowData.receiptDate)   }</span>;
    }

    transactionIdBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        return (
            <div className="transaction-info">
                <div className="transaction-id">{rowData.transctionId || rowData._id}</div>
                <div className="receipt-number">Receipt: {rowData.receiptNo}</div>
                <div className="entry-date">Entry: {this.formatDate(rowData.receiptDate)}</div>
            </div>
        );
    }

    receivingAccountBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        return (
            <div className="account-info">
                <div className="bank-name">{rowData.destinationBankName  || 'N/A'}</div>
                <div className="account-number">A/C: {rowData.accountNo  || 'N/A'}</div>
                <div className="school-name">{rowData.branchName  || 'N/A'}</div>
            </div>
        );
    }

    paymentModeBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        
        const paymentModeId = rowData.paymentModeId;
        const paymentMode = rowData.paymentMode;
        
        return (
            <div className="payment-info">
                <div className="payment-badge">
                    <Badge value={paymentMode} severity="success" />
                </div>
                
                {/* Cash - Show destination bank */}
                {paymentModeId === '74b95cc8-9736-44a0-b2df-325595783c3b' && rowData.destinationBank && (
                    <div className="payment-details">
                        <div>Bank: {rowData.destinationBankName || rowData.destinationBank}</div>
                    </div>
                )}
                
                {/* Cheque - Show cheque details */}
                {paymentModeId === '1c98862e-46dc-4454-a120-7d15e2e56e2f' && (
                    <div className="payment-details">
                        {rowData.chequeNo && <div>Cheque No: {rowData.chequeNo}</div>}
                        {rowData.chequeIssuedTo && <div>Issued To: {rowData.chequeIssuedTo}</div>}
                        {rowData.chequeDate && <div>Date: {this.formatDate(rowData.chequeDate)}</div>}
                        {rowData.bankName && <div>Bank: {rowData.bankName}</div>}
                        {rowData.bankBranchName && <div>Branch: {rowData.bankBranchName}</div>}
                    </div>
                )}
                
                {/* DD (Demand Draft) - Show DD details */}
                {paymentModeId === '9d08ebab-2d3e-4a30-8460-4e4092fe30a1' && (
                    <div className="payment-details">
                        {rowData.ddNo && <div>DD No: {rowData.ddNo}</div>}
                        {rowData.ddIssuedTo && <div>Issued To: {rowData.ddIssuedTo}</div>}
                        {rowData.ddDate && <div>Date: {this.formatDate(rowData.ddDate)}</div>}
                        {rowData.bankName && <div>Bank: {rowData.bankName}</div>}
                        {rowData.bankBranchName && <div>Branch: {rowData.bankBranchName}</div>}
                    </div>
                )}
                
                {/* Online Payment, Credit Card, Debit Card, QR Code - Show transaction ID */}
                {(paymentModeId === '1b1a76b4-c538-4968-a20b-0e5a3b4a1c54' ||
                  paymentModeId === 'b9e46415-1b11-4377-9105-58b98c230935' ||
                  paymentModeId === '274a4416-2454-4f3a-b741-89c67361ae1f' ||
                  paymentModeId === '32b180cf-7727-46ef-b2a8-e7b1b19fd489') && (
                    <div className="payment-details">
                        {rowData.transctionId && <div>Transaction ID: {rowData.transctionId}</div>}
                    </div>
                )}
                
                {/* Credit Card / Debit Card - Show additional card details */}
                {(paymentModeId === 'b9e46415-1b11-4377-9105-58b98c230935' ||
                  paymentModeId === '274a4416-2454-4f3a-b741-89c67361ae1f') && (
                    <div className="payment-details">
                        {rowData.cardNo && <div>Card No: ****{rowData.cardNo.slice(-4)}</div>}
                        {rowData.approvalCode && <div>Approval Code: {rowData.approvalCode}</div>}
                        {rowData.rrNumber && <div>RR Number: {rowData.rrNumber}</div>}
                        {rowData.userBankName && <div>Bank: {rowData.userBankName}</div>}
                    </div>
                )}
            </div>
        );
    }

    feeTypeBodyTemplate(rowData) {
        return (
            <div className="fee-type-info" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div className="fee-type-name">{rowData.feeDetail.feeType}</div>
                {rowData.feeDetail.paymentTerm && (
                    <div className="payment-term"> - {rowData.feeDetail.paymentTerm}</div>
                )}
            </div>
        );
    }

    paidAmountBodyTemplate(rowData) {
        return <span>₹{rowData.feeDetail.amountPaying?.toFixed(2) || '0.00'}</span>;
    }

    totalBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        return <span>₹{rowData.amountPaid?.toFixed(2) || '0.00'}</span>;
    }

    actionsBodyTemplate(rowData) {
        if (!rowData.isFirstRow) return null;
        return (
            <div className="action-buttons flex flex-column justify-content-start align-items-center gap-2">
                <Button
                    label="Print"
                    className="p-button-sm view-btn"
                    onClick={() => {
                        console.log('View button clicked');
                        this.setState({
                            isEnableReceipt: true,
                            receiptData: rowData
                        });
                    }}
                />
            </div>
        );
    }

    rowClassName(rowData) {
        let className = '';
        if (rowData.isLastRow) {
            className += 'transaction-group-last-row';
        }
        if (rowData.isFirstRow) {
            className += ' transaction-group-first-row';
        }
        return className.trim();
    }

    render() {
        const { flattenedData, isLoading } = this.state;
        const grandTotal = this.calculateGrandTotal();
        console.log('TransactionsTable rendering with transactions:', flattenedData.length, 'Grand Total:', grandTotal);

        return (
            <>
                <div className="transactions-table-container">
                <div className="info-note">
                    <i className="pi pi-info-circle"></i>
                    <span>Note : Transactions which are showing not including of Other fee Collections, School Store Transactions.</span>
                </div>

                {isLoading && <LoadingComponent />}
                
                {!isLoading && (
                <div className="transactions-table-wrapper">
                    <div ref={this.dataScrollRef}>
                        <DataTable 
                            value={flattenedData}
                            rowGroupMode="rowspan" 
                            groupRowsBy="receiptNo"
                            sortMode="single" 
                            sortField="receiptNo" 
                            sortOrder={1}
                            scrollable 
                            scrollHeight="flex"
                            className="transactions-table"
                            showGridlines
                            rowClassName={this.rowClassName}
                            emptyMessage="No transaction records available for this student"
                        >
                        <Column 
                            header="S.No" 
                            body={this.snoBodyTemplate}
                            headerClassName="sno-header"
                            bodyClassName="sno-cell"
                            style={{ width: '80px', textAlign: 'center' }}
                        />
                        <Column 
                            header="Receipt Date" 
                            body={this.receiptDateBodyTemplate}
                            headerClassName="receipt-date-header"
                            bodyClassName="receipt-date-cell"
                            style={{ width: '120px' }}
                        />
                        <Column 
                            header="TransactionID / Receipt No" 
                            body={this.transactionIdBodyTemplate}
                            headerClassName="transaction-id-header"
                            bodyClassName="transaction-id-cell"
                            style={{ width: '250px' }}
                        />
                        <Column 
                            header="Receiving Account" 
                            body={this.receivingAccountBodyTemplate}
                            headerClassName="receiving-account-header"
                            bodyClassName="receiving-account-cell"
                            style={{ width: '280px' }}
                        />
                        <Column 
                            header="Payment Mode" 
                            body={this.paymentModeBodyTemplate}
                            headerClassName="payment-mode-header"
                            bodyClassName="payment-mode-cell"
                            style={{ width: '280px' }}
                        />
                        <Column 
                            header="Fee Type" 
                            body={this.feeTypeBodyTemplate}
                            headerClassName="fee-type-header"
                            bodyClassName="fee-type-cell"
                            style={{ width: '300px' }}
                        />
                        <Column 
                            header="Paid Amount" 
                            body={this.paidAmountBodyTemplate}
                            headerClassName="paid-amount-header"
                            bodyClassName="paid-amount-cell"
                            style={{ width: '150px', textAlign: 'right' }}
                        />
                        <Column 
                            header="Total" 
                            body={this.totalBodyTemplate}
                            headerClassName="total-header"
                            bodyClassName="total-cell"
                            style={{ width: '150px', textAlign: 'right' }}
                        />
                        <Column 
                            header="Actions" 
                            body={this.actionsBodyTemplate}
                            headerClassName="actions-header"
                            bodyClassName="actions-cell"
                            style={{ width: '250px' }}
                        />
                    </DataTable>
                    </div>

                    {flattenedData.length > 0 && (
                    <div ref={this.grandTotalScrollRef} style={{ overflow: 'hidden' }}>
                        <div style={{ width: 'max-content' }}>
                            <DataTable
                                value={[{ total: grandTotal }]}
                                className="transactions-grand-total-table"
                                showGridlines
                            >
                                <Column header="" body={() => ''} style={{ minWidth: '80px', width: '80px' }} />
                                <Column header="" body={() => ''} style={{ minWidth: '120px', width: '120px' }} />
                                <Column header="" body={() => ''} style={{ minWidth: '250px', width: '250px' }} />
                                <Column header="" body={() => ''} style={{ minWidth: '280px', width: '280px' }} />
                                <Column header="" body={() => ''} style={{ minWidth: '280px', width: '280px' }} />
                                <Column header="" body={() => <strong style={{ color: '#076EFF' }}>GRAND TOTAL :</strong>} style={{ minWidth: '300px', width: '300px' }} />
                                <Column header="" body={() => <strong style={{ color: '#076EFF' }}>₹{grandTotal?.toFixed(2)}</strong>} style={{ minWidth: '150px', width: '150px' }} />
                                <Column header="" body={() => ''} style={{ minWidth: '150px', width: '150px' }} />
                                <Column header="" body={() => ''} style={{ minWidth: '250px', width: '250px' }} />
                            </DataTable>
                        </div>
                    </div>
                    )}
                </div>
                )}
            </div>
            
            {this.state.isEnableReceipt && (
                <NewFeeReciept
                    receiptData={this.state?.receiptData}
                    hideFeeReciept={() => {
                        this.setState({
                            isEnableReceipt: false,
                            receiptData: []
                        });
                    }}
                />
            )}
        </>
        );
    }
}
